<script setup>
import { filename } from 'pathe/utils'
import { positionList } from '@/client'
import { useLohndatenStore } from '@/stores/lohndaten'
import { inject, onMounted, ref } from 'vue'
import { useTextStore } from '@/stores/texts'

import { client } from '@/lohnrechner'
import { nextTick } from 'vue'

const store = useLohndatenStore()
const emit = defineEmits(['complete'])

const glob = import.meta.glob('@/assets/positions/*.png', { eager: true })

const icons = Object.fromEntries(
  Object.entries(glob).map(([key, value]) => [filename(key), value.default])
)

const noLeadPosition = ref(null)
const leadPositions = ref(null)
const selectedIndex = ref(0)

function updateItems() {
  if (!store.beruf) {
    return
  }
  positionList({ query: { job_id: store.beruf }, client }).then((list) => {
    let positions = list.data.results.sort((a, b) => -a.code.localeCompare(b.code))
    noLeadPosition.value = positions[0]
    if (store.stellung) {
      for (let pos of positions) {
        if (pos.pk == store.stellung && !pos.available) {
          store.stellung = null
          break
        }
      }
    }
    if (!store.stellung && noLeadPosition.value.available) {
      store.stellung = noLeadPosition.value.pk
    }
    if (!noLeadPosition.value.available) {
      store.is_lead = true
    }
    leadPositions.value = positions.slice(1)
  })
}

onMounted(() => {
  updateItems()
})

function selectStep(index) {
  store.stellung = leadPositions.value[index].pk

  emit('complete')
}

function isLead() {
  return store.is_lead
}

function onIsLead() {
  store.is_lead = true
}

function onIsNotLead() {
  if (!noLeadPosition.value.available) {
    return
  }
  store.stellung = noLeadPosition.value.pk
  store.is_lead = false
  emit('complete')
}

let beruf
store.$subscribe(() => {
  if (beruf != store.beruf) {
    setTimeout(() => {
      updateItems()
    }, 1)
  }
  beruf = store.branche
})

const textStore = useTextStore()

let lang = textStore.language
textStore.$subscribe((mutation, state) => {
  if (state.language != lang) {
    nextTick(() => {
      updateItems()
    })
  }
  lang = state.language
})

const t = inject('t')
</script>
<template>
  <StaticElement name="isLead">
    <div class="lead-field">
      <label>{{ t('vorgesetzt-frage') }}</label>
      <div class="buttons">
        <button
          :class="{ active: store.is_lead === false, disabled: !noLeadPosition?.available }"
          @click.prevent="onIsNotLead"
        >
          {{ t('nein') }}
        </button>
        <button :class="{ active: store.is_lead === true }" @click.prevent="onIsLead">
          {{ t('ja') }}
        </button>
      </div>

      <label v-if="store.is_lead === true" class="lead-label">{{ t('position-frage') }}</label>
      <div class="isLead" v-if="isLead()">
        <div class="positions">
          <div
            v-for="(position, index) in leadPositions"
            :key="position.pk"
            :class="{ active: store.stellung == position.pk, disabled: !position.available }"
            @click="position.available ? selectStep(index) : null"
          >
            <label>{{ position.name }}</label>

            <img :src="icons['ber_' + position.code]" style="padding: 1rem 0" />
            <span></span>
          </div>
        </div>
      </div>
    </div>

    <div v-if="isLead && false" class="leadChooser">
      <div class="head">
        <button class="close" @click="isLead = false">
          <svg
            width="34"
            height="34"
            viewBox="0 0 34 34"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M26.4999 9.10571L24.551 7.15674L16.8242 14.8835L9.09741 7.15674L7.14844 9.10571L14.8752 16.8325L7.14844 24.5593L9.09741 26.5082L16.8242 18.7815L24.551 26.5082L26.4999 24.5593L18.7732 16.8325L26.4999 9.10571Z"
              fill="#34333C"
            />
          </svg>
        </button>
        <label>{{ t('position-frage') }}</label>
      </div>
      <div class="wrapper">
        <div style="overflow: hidden; width: 100%; position: absolute; left: 0">
          <div
            class="position-scroller"
            v-touch:swipe="onSwipe"
            :style="{ transform: 'translate(' + -200 * selectedIndex + 'px)' }"
          ></div>
        </div>
      </div>
      <div class="buttons chooser">
        <button @click.prevent="prevStep" :disabled="selectedIndex == 0">
          <svg
            width="20"
            height="21"
            viewBox="0 0 20 21"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M11.6191 19.3242L2.43392 10.1392L11.6191 0.954032"
              stroke="#858585"
              stroke-width="2.54776"
            />
            <line
              x1="3.76758"
              y1="10.1333"
              x2="19.703"
              y2="10.1333"
              stroke="#858585"
              stroke-width="2.54776"
            />
          </svg>
        </button>
        <button @click.prevent="nextStep" :disabled="selectedIndex == leadPositions.length - 1">
          <svg
            width="20"
            height="21"
            viewBox="0 0 20 21"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M8.83594 0.954102L18.0212 10.1391L8.83596 19.3243"
              stroke="#858585"
              stroke-width="2.54776"
            />
            <line
              x1="16.6855"
              y1="10.145"
              x2="0.750096"
              y2="10.145"
              stroke="#858585"
              stroke-width="2.54776"
            />
          </svg>
        </button>
      </div>
    </div>
  </StaticElement>
</template>
<style></style>

<style scoped>
.lead-field {
  background-color: var(--color-grey);
  display: flex;
  flex-direction: column;
  align-items: center;
  border-radius: 29px;
}

button {
  background: transparent;
  border: solid var(--color-text) 1px;
  border-radius: 64px;
  height: 60px;
  flex: 1 1 50%;
  cursor: pointer;
  font-family: Theinhardt, sans-serif;
  font-size: 1rem;
}

button.active {
  font-weight: bold;
  border-width: 2px;
}

.buttons.chooser button {
  border: none;
}

.buttons {
  justify-content: center;
  padding: 0 8px;
  gap: 0.5em;
  margin-top: 20px;
  display: flex;
  align-self: stretch;
}

.lead-field {
  padding: 2rem 2rem 2rem 2rem;
}

.leadChooser {
  box-shadow: 0px 1px 4px 0px rgba(0, 0, 0, 0.25);
  border-radius: 41px;
  padding-bottom: 30px;
  background-color: white;
  position: fixed;
  right: 50%;
  top: 50%;
  transform: translate(50%, -50%);
  width: min(80vw, 800px);
}

.positions {
  animation-name: backInDownChooser;
  animation-duration: 1000ms;
  animation-fill-mode: both;
}

@keyframes backInDownChooser {
  0% {
    transform: translateY(-1200px);
    opacity: 0;
  }

  80% {
    transform: translateY(0px);
  }

  100% {
    transform: translateY(0px);
    opacity: 1;
  }
}

button:disabled svg * {
  stroke: rgb(221, 221, 221);
}

.isLead {
  align-self: stretch;
}

.lead-label {
  padding-top: 1rem;
}

.positions div.active {
  border: solid #34333c 2px;
}

.positions div:hover {
  transform: scale(0.95);
  box-shadow: none;
}

.positions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  gap: 12px;
  align-self: stretch;

  padding-top: 0.5rem;
}

.positions div {
  display: flex;
  flex-direction: column;
  background: white;
  padding: 5px;
  border-radius: 12px;
  box-shadow: rgba(0, 0, 0, 0.1) 0px 0px 5px;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  transition:
    transform 0.3s,
    box-shadow 0.3s;
}

.positions div img {
  max-width: 6em;
}

.positions label {
  text-align: center;
  padding: 0.1rem 0.2rem;
}

.disabled.disabled {
  filter: grayscale();
  cursor: not-allowed;
  box-shadow: none;
}

.disabled.disabled:hover {
  transform: none;
  box-shadow: none;
}
</style>
